import { useCallback, useEffect, useRef } from 'react';

import { AnalyticsEvent, track } from '@bonusly/analytics';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { getAudienceSegment, isTrialSegment } from '../lib/suggested-prompts';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

/**
 * Tracks Bizy side-panel open/close transitions in Amplitude.
 * Shared by useBizyLayout (user-facing) and useBizyAdminLayout (admin).
 *
 * Returns a `trackPanelAutoOpened` callback so callers can emit an
 * explicit "Bizy Panel Auto Opened" event with the same enrichment
 * (surface, is_trial) used for the regular open event. The general
 * ChatOpened/ChatSeen events still fire from the isOpen transition.
 */
export function useBizyPanelTracking(isPanelOpen: boolean) {
	const prevPanelOpenRef = useRef(isPanelOpen);
	const company = useCurrentCompany();
	const user = useCurrentUser();
	const isTrial = company ? isTrialSegment(getAudienceSegment(company.status, user?.admin ?? false)) : false;
	const isTrialRef = useRef(isTrial);
	isTrialRef.current = isTrial;

	useEffect(() => {
		if (prevPanelOpenRef.current === isPanelOpen) return;
		prevPanelOpenRef.current = isPanelOpen;

		const trigger = useBizySidePanelStore.getState().lastTrigger;
		const surface = useBizyPageContextStore.getState().context.pageType;
		const baseProps = { path: window.location.pathname, trigger, surface, is_trial: isTrialRef.current };

		if (isPanelOpen) {
			track(AnalyticsEvent.Bizy.ChatOpened, baseProps);
			track(AnalyticsEvent.Bizy.ChatSeen, baseProps);
		} else {
			track(AnalyticsEvent.Bizy.ChatClosed, baseProps);
		}
	}, [isPanelOpen]);

	const trackPanelAutoOpened = useCallback(() => {
		const surface = useBizyPageContextStore.getState().context.pageType;
		track(AnalyticsEvent.Bizy.PanelAutoOpened, {
			path: window.location.pathname,
			surface,
			is_trial: isTrialRef.current,
		});
	}, []);

	return { trackPanelAutoOpened };
}
